import Agilite from 'agilite'
import store from '../../../store'
import CoreEnums from '../../../core/utils/enums'
import { createLookup } from '../../lib/utils'
import { getDataWithSync } from '../../../db/dbSync'
import SystemUsersEnums from '../../Admin/system-users/utils/enums'
import { STORES } from '../../../db/indexedDB'
import { createBooking } from '../../Bookings/utils/utils'
import { executeBooking, registerBooking } from '../../Bookings/utils/bpm-utils'
import { handleError } from '../../lib/errorHandler'
import { getSearchCoordinatesFromPlaceId } from '../utils/locationUtils'
import axios from 'axios'

const agilite = new Agilite({
  apiServerUrl: process.env.REACT_APP_AGILITE_API_URL,
  apiKey: process.env.REACT_APP_AGILITE_API_KEY
})

export const searchDoctors = async (filter) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        // Build query object from filters
        const query = {
          'extraData.profession': 'doctor',
          'extraData.isActive': true,
          'extraData.clinics': { $exists: true, $ne: [] }
        }

        if (filter.searchType === 'general_practitioner' || filter.searchType === 'virtual_consultation') {
          query['professionalType'] = 'general_practitioner'
        }

        if (filter.searchType === 'vitality_health_check') {
          query['professionalType'] = { $in: ['general_practitioner', 'nurse'] }
        }

        // Add gender filter if specified
        if (filter?.gender && filter.gender !== 'any') {
          query.gender = filter.gender
        }

        // Add search type filter if specified (replaces specialty filter)
        if (filter?.searchType && filter.searchType !== 'general_practitioner') {
          // Map search type display names to database values
          const searchTypeMap = {
            cardiologist: 'cardiologist',
            dermatologist: 'dermatologist',
            neurologist: 'neurologist',
            psychiatrist: 'psychiatrist',
            gynaecologist: 'gynaecologist',
            dentist: 'dentist',
            general_practitioner: 'general_practitioner'
          }

          const searchTypeValue = searchTypeMap[filter.searchType] || filter.searchType.toLowerCase()
          // TODO: Address this
          // if (searchTypeValue !== 'general_practitioner') {
          //   query['extraData.specialty'] = searchTypeValue
          // }
        }

        // Add legacy specialty filter support (for backward compatibility)
        if (filter?.specialty && filter.specialty !== 'General Practitioner') {
          const specialtyMap = {
            Cardiologist: 'cardiologist',
            Dermatologist: 'dermatologist',
            Neurologist: 'neurologist',
            Psychiatrist: 'psychiatrist',
            Gynaecologist: 'gynaecologist',
            Dentist: 'dentist'
          }

          const specialtyValue = specialtyMap[filter.specialty] || filter.specialty.toLowerCase()
          query['extraData.specialty'] = specialtyValue
        }

        // Add medical aids filter if specified
        if (filter?.medicalAids && filter.medicalAids.length > 0) {
          query['extraData.acceptedMedicalAids'] = { $in: filter.medicalAids }
        }

        // Add minimum rating filter if specified
        if (filter?.minRating) {
          query.rating = { $gte: parseFloat(filter.minRating) }
        }

        // Get user location if place ID is provided
        let userLocation = null
        if (filter?.placeId) {
          try {
            userLocation = await getSearchCoordinatesFromPlaceId(filter.placeId)
            if (userLocation.success) {
            } else {
              console.warn('Failed to get user location:', userLocation.error)
            }
          } catch (error) {
            console.error('Error getting user location:', error)
          }
        }

        // Prepare complete filter data for Node-RED
        const nodeRedFilter = {
          // Search filters
          consultType: filter?.consultType || [],
          timeSlot: filter?.timeSlot || '',
          languages: filter?.languages || [],
          gender: filter?.gender || '',
          medicalAids: filter?.medicalAids || [],
          minRating: filter?.minRating || '',
          searchType: filter?.searchType || 'general_practitioner',

          // Location data
          placeId: filter?.placeId || '',
          location: filter?.location || null,
          maxDistance: filter?.maxDistance || 25,
          userLocation: userLocation,

          // Legacy support
          specialty: filter?.specialty || '',
          address: filter?.address || ''
        }

        // Get current entity information for network filtering
        const currentEntity = store.getState().core.entity
        const allowOutsideNetwork = currentEntity?.allowOutsideNetwork !== false // Default to true for backward compatibility

        // Use Node-RED endpoint instead of local aggregation
        const searchData = {
          qry: query,
          page: filter?.page || 1,
          pageLimit: filter?.pageLimit || 20,
          filter: {
            ...nodeRedFilter,
            // Add entity network filtering information
            allowOutsideNetwork: allowOutsideNetwork,
            currentEntityRef: currentEntity?._id
          },
          team_id: process.env.REACT_APP_AGILITE_TEAM_ID
        }

        const response = await handleSearchRequest(searchData)

        resolve(response)
      } catch (e) {
        console.error('Search error:', e)
        reject(e)
      }
    })()
  })
}

/**
 * Get doctors for a specific page
 * @param {number} page - Page number
 * @param {Object} filters - Current search filters
 * @returns {Promise<Object>} Paginated results
 */
export const getDoctorsPage = async (page, filters) => {
  try {
    // Use the same search function but with updated page
    const updatedFilters = {
      ...filters,
      page: page,
      pageLimit: 20
    }

    const response = await searchDoctors(updatedFilters)

    // Handle response format with doctorCount
    let results, totalResults
    if (response && response.results) {
      results = response.results
      totalResults = response.doctorCount || response.results.length
    } else {
      results = response || []
      totalResults = results.length
    }

    return {
      results: results,
      pagination: {
        currentPage: page,
        totalPages: Math.ceil(totalResults / 20),
        totalResults: totalResults,
        resultsPerPage: 20
      },
      success: true
    }
  } catch (error) {
    console.error('Error getting doctors page:', error)
    throw error
  }
}

export const getDoctorProfile = async (doctorId) => {
  try {
    await new Promise((resolve) => setTimeout(resolve, 300))

    return {
      id: doctorId,
      name: 'Dr. Sample Doctor',
      specialty: 'General Practitioner',
      bio: 'Experienced general practitioner with 10+ years of practice...',
      qualifications: ['MB ChB', 'FCFP'],
      languages: ['English', 'Afrikaans'],
      availability: {
        today: ['09:00', '10:30', '14:00', '16:30'],
        tomorrow: ['08:00', '11:00', '15:30']
      },
      success: true
    }
  } catch (error) {
    console.error('Error getting doctor profile:', error)
    throw error
  }
}

export const getDoctorAvailability = async (filter) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        // Use Node-RED endpoint for availability fetching
        const availabilityData = {
          medicalProfessional: filter.medicalProfessional,
          clinicRef: filter.clinicRef,
          startDate: filter.startDate,
          endDate: filter.endDate,
          filter: filter,
          team_id: process.env.REACT_APP_AGILITE_TEAM_ID
        }

        const response = await handleIndividualAvailabilityFetch(availabilityData)

        resolve(response)
      } catch (e) {
        console.error('Error fetching doctor availability:', e)
        if (e.response?.data?.errorMessage) {
          reject(e.response.data.errorMessage)
        } else if (e.message) {
          reject(e.message)
        } else {
          reject(CoreEnums.errorMessages.UNKNOWN_ERROR)
        }
      }
    })()
  })
}

export const bookAppointment = async (appointmentData) => {
  const bpmKey = 'patient_journey'
  const processUser = store.getState().auth.agiliteUser.firstName + ' ' + store.getState().auth.agiliteUser.lastName

  let processRecord = await registerBooking(bpmKey, processUser)
  processRecord = await executeBooking(bpmKey, processRecord.recordId, 'submit', processUser, processRecord.key)

  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        // If it is a Home Visit and not a Virtual Consultation, we need to skip the checkin step
        processRecord = await executeBooking(bpmKey, processRecord.recordId, 'submit', processUser, processRecord.key)

        let newBooking = await createBooking({
          ...appointmentData,
          processRef: processRecord.recordId,
          status: processRecord.processStage
        })

        resolve(newBooking)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

export const getFilterOptions = async () => {
  try {
    await new Promise((resolve) => setTimeout(resolve, 300))

    return {
      specialties: ['General Practitioner', 'Cardiologist', 'Dermatologist', 'Neurologist', 'Psychiatrist'],
      languages: ['English', 'Afrikaans', 'Zulu', 'Xhosa', 'Sotho'],
      medicalAids: ['Discovery Health', 'Medshield', 'Momentum Health', 'Bestmed', 'Bonitas'],
      consultTypes: ['In-Person Consultation', 'Virtual Consultation', 'Home Visit'],
      success: true
    }
  } catch (error) {
    console.error('Error getting filter options:', error)
    throw error
  }
}

/**
 * @deprecated This function is deprecated. Use searchDoctors() instead which uses Node-RED aggregation pipeline.
 * This function is kept for backward compatibility and may be removed in future versions.
 */
export const fetchDoctorsProfilesAggregate = (
  qry,
  page = null,
  pageLimit = null,
  projection,
  fetchAll,
  isPwa = false,
  filter = null
) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        let response = null
        // Build pipeline
        const pipeline = []

        // Add initial match stage if query exists - this is critical for performance
        if (qry && Object.keys(qry).length > 0) {
          pipeline.push({ $match: qry })
        }

        // Add sorting before pagination to ensure consistent results
        pipeline.push({ $sort: { dateCreated: -1 } })

        // Add pagination early in the pipeline to reduce the number of documents processed
        if (page && pageLimit) {
          pipeline.push({ $skip: (page - 1) * pageLimit }, { $limit: parseInt(pageLimit) })
        }

        // Add clinics lookup
        pipeline.push(
          // Convert string IDs to ObjectIds
          {
            $addFields: {
              clinicRefObjectId: {
                $cond: [
                  {
                    $or: [
                      { $eq: ['$extraData.clinics', null] },
                      { $eq: ['$extraData.clinics', []] },
                      { $eq: [{ $size: { $ifNull: ['$extraData.clinics', []] } }, 0] }
                    ]
                  },
                  null,
                  { $toObjectId: { $arrayElemAt: ['$extraData.clinics', 0] } }
                ]
              }
            }
          },

          // Main member lookup
          createLookup(
            `${process.env.REACT_APP_AGILITE_TEAM_ID}_admin_clinics`,
            'clinicRefObjectId',
            '_id',
            'clinicRecord',
            {
              name: 1,
              address: 1,
              phoneNumber: 1,
              physicalAddress: 1,
              email: 1,
              website: 1,
              bpmKey: 1,
              afterHourFee: 1,
              geolocation: 1 // Include geolocation for distance calculations
            }
          ),

          { $unwind: { path: '$clinicRecord', preserveNullAndEmptyArrays: true } }
        )

        pipeline.push(
          // Convert string IDs to ObjectIds
          {
            $addFields: {
              medProfRef: { $toString: '$_id' }
            }
          },

          createLookup(
            `${process.env.REACT_APP_AGILITE_TEAM_ID}_bookings`,
            'medProfRef',
            'medicalProfRef',
            'existingBookings',
            {
              bookingDate: 1,
              startTime: 1,
              endTime: 1,
              status: 1
            }
          )
        )

        // Add availability lookup
        pipeline.push(
          // Convert string IDs to ObjectIds
          {
            $addFields: {
              userId: { $toString: '$_id' }
            }
          },

          // Main member lookup
          createLookup(
            `${process.env.REACT_APP_AGILITE_TEAM_ID}_availabilities`,
            'userId',
            'medicalProfessional',
            'availability',
            {
              availability: 1
            }
          ),

          // Unwind main member array
          { $unwind: { path: '$availability' } }
        )

        // Group back together to avoid duplicate doctors
        pipeline.push({
          $group: {
            _id: '$_id',
            // Keep all original doctor fields
            firstName: { $first: '$firstName' },
            lastName: { $first: '$lastName' },
            email: { $first: '$email' },
            extraData: { $first: '$extraData' },
            dateCreated: { $first: '$dateCreated' },
            dateModified: { $first: '$dateModified' },
            clinicRecord: { $first: '$clinicRecord' },
            existingBookings: { $first: '$existingBookings' },
            // Collect all availability records into an array
            availability: { $push: '$availability.availability' }
          }
        })

        // Flatten the availability array since it's nested
        pipeline.push({
          $addFields: {
            availability: {
              $reduce: {
                input: '$availability',
                initialValue: [],
                in: { $concatArrays: ['$$value', '$$this'] }
              }
            }
          }
        })

        // First, filter to only include future dates (today and onwards) - using South Africa timezone (UTC+2)
        pipeline.push({
          $addFields: {
            availability: {
              $filter: {
                input: '$availability',
                as: 'avail',
                cond: {
                  $gte: [
                    '$$avail.date',
                    {
                      $dateToString: {
                        format: '%Y-%m-%d',
                        date: {
                          $dateAdd: {
                            startDate: '$$NOW',
                            unit: 'hour',
                            amount: 2
                          }
                        }
                      }
                    }
                  ]
                }
              }
            }
          }
        })

        // Filter out cancelled bookings first
        pipeline.push({
          $addFields: {
            activeBookings: {
              $filter: {
                input: { $ifNull: ['$existingBookings', []] },
                as: 'booking',
                cond: { $ne: ['$$booking.status', 'cancelled'] }
              }
            }
          }
        })

        // Filter out past time slots and booking conflicts, with optional time preference filtering
        pipeline.push({
          $addFields: {
            availability: {
              $map: {
                input: '$availability',
                as: 'dayAvail',
                in: {
                  $mergeObjects: [
                    '$$dayAvail',
                    {
                      timeSlots: {
                        $filter: {
                          input: '$$dayAvail.timeSlots',
                          as: 'slot',
                          cond: {
                            $and: [
                              // First check: Time slot must be in the future (for today's date)
                              {
                                $cond: [
                                  // If this is today's date (South Africa timezone)
                                  {
                                    $eq: [
                                      '$$dayAvail.date',
                                      {
                                        $dateToString: {
                                          format: '%Y-%m-%d',
                                          date: {
                                            $dateAdd: {
                                              startDate: '$$NOW',
                                              unit: 'hour',
                                              amount: 2
                                            }
                                          }
                                        }
                                      }
                                    ]
                                  },
                                  // Check if slot is at least 30 minutes from now (adjusted for South Africa timezone UTC+2)
                                  {
                                    $gte: [
                                      '$$slot.time',
                                      {
                                        $dateToString: {
                                          format: '%H:%M',
                                          date: {
                                            $dateAdd: {
                                              startDate: {
                                                $dateAdd: {
                                                  startDate: '$$NOW',
                                                  unit: 'hour',
                                                  amount: 2
                                                }
                                              },
                                              unit: 'minute',
                                              amount: 30
                                            }
                                          }
                                        }
                                      }
                                    ]
                                  },
                                  // If it's a future date, this check passes
                                  true
                                ]
                              },
                              // Second check: Slot must not conflict with existing bookings
                              {
                                $cond: [
                                  // If there are no active bookings, slot is available
                                  { $eq: [{ $size: { $ifNull: ['$activeBookings', []] } }, 0] },
                                  true,
                                  // Otherwise, check for conflicts
                                  {
                                    $not: {
                                      $anyElementTrue: {
                                        $map: {
                                          input: '$activeBookings',
                                          as: 'booking',
                                          in: {
                                            $and: [
                                              // Booking is on the same date (extract date part from ISO string)
                                              {
                                                $eq: [
                                                  {
                                                    $dateToString: { format: '%Y-%m-%d', date: '$$booking.bookingDate' }
                                                  },
                                                  '$$dayAvail.date'
                                                ]
                                              },
                                              // Slot time is between booking start and end time
                                              { $gte: ['$$slot.time', '$$booking.startTime'] },
                                              { $lt: ['$$slot.time', '$$booking.endTime'] }
                                            ]
                                          }
                                        }
                                      }
                                    }
                                  }
                                ]
                              },
                              // Third check: Time preference filtering (if timeSlot filter is provided)
                              ...(filter?.timeSlot
                                ? [
                                    {
                                      $cond: [
                                        { $eq: [filter.timeSlot, 'morning'] },
                                        // Morning: 6AM - 12PM
                                        {
                                          $and: [{ $gte: ['$$slot.time', '06:00'] }, { $lt: ['$$slot.time', '12:00'] }]
                                        },
                                        {
                                          $cond: [
                                            { $eq: [filter.timeSlot, 'afternoon'] },
                                            // Afternoon: 12PM - 6PM
                                            {
                                              $and: [
                                                { $gte: ['$$slot.time', '12:00'] },
                                                { $lt: ['$$slot.time', '18:00'] }
                                              ]
                                            },
                                            {
                                              $cond: [
                                                { $eq: [filter.timeSlot, 'evening'] },
                                                // Evening: 6PM - 12AM
                                                {
                                                  $and: [
                                                    { $gte: ['$$slot.time', '18:00'] },
                                                    { $lt: ['$$slot.time', '24:00'] }
                                                  ]
                                                },
                                                true // No time preference filter
                                              ]
                                            }
                                          ]
                                        }
                                      ]
                                    }
                                  ]
                                : [])
                            ]
                          }
                        }
                      }
                    }
                  ]
                }
              }
            }
          }
        })

        // Filter out days with no available time slots (empty timeSlots arrays)
        pipeline.push({
          $addFields: {
            availability: {
              $filter: {
                input: '$availability',
                as: 'dayAvail',
                cond: { $gt: [{ $size: '$$dayAvail.timeSlots' }, 0] }
              }
            }
          }
        })

        // If time preference filter is specified, only show doctors who have availability in that time range
        if (filter?.timeSlot) {
          pipeline.push({
            $match: {
              availability: { $ne: [], $exists: true }
            }
          })
        }

        // Now find the closest date that actually has available slots
        pipeline.push({
          $addFields: {
            availability: {
              $cond: [
                { $gt: [{ $size: '$availability' }, 0] },
                {
                  $let: {
                    vars: {
                      closestViableDate: {
                        $min: '$availability.date'
                      }
                    },
                    in: {
                      $filter: {
                        input: '$availability',
                        as: 'avail',
                        cond: { $eq: ['$$avail.date', '$$closestViableDate'] }
                      }
                    }
                  }
                },
                []
              ]
            }
          }
        })

        // Extract only the first available slot and rename it to nextAvailableSlot
        pipeline.push({
          $addFields: {
            nextAvailableSlot: {
              $cond: [
                { $gt: [{ $size: '$availability' }, 0] },
                {
                  $let: {
                    vars: {
                      firstDay: { $arrayElemAt: ['$availability', 0] }
                    },
                    in: {
                      $cond: [
                        { $gt: [{ $size: '$$firstDay.timeSlots' }, 0] },
                        {
                          date: '$$firstDay.date',
                          timeSlot: { $arrayElemAt: ['$$firstDay.timeSlots', 0] }
                        },
                        null
                      ]
                    }
                  }
                },
                null
              ]
            }
          }
        })

        // DEBUG: Add debugging info to see what's happening with time filtering
        pipeline.push({
          $addFields: {
            debugTimeInfo: {
              serverTime: { $dateToString: { format: '%H:%M', date: '$$NOW' } },
              southAfricaTime: {
                $dateToString: {
                  format: '%H:%M',
                  date: {
                    $dateAdd: {
                      startDate: '$$NOW',
                      unit: 'hour',
                      amount: 2
                    }
                  }
                }
              },
              southAfricaTimePlus30: {
                $dateToString: {
                  format: '%H:%M',
                  date: {
                    $dateAdd: {
                      startDate: {
                        $dateAdd: {
                          startDate: '$$NOW',
                          unit: 'hour',
                          amount: 2
                        }
                      },
                      unit: 'minute',
                      amount: 30
                    }
                  }
                }
              },
              currentDate: {
                $dateToString: {
                  format: '%Y-%m-%d',
                  date: {
                    $dateAdd: {
                      startDate: '$$NOW',
                      unit: 'hour',
                      amount: 2
                    }
                  }
                }
              },
              slotDate: '$nextAvailableSlot.date',
              slotTime: '$nextAvailableSlot.timeSlot.time'
            }
          }
        })

        // Remove the full availability array and booking fields since we only need nextAvailableSlot
        pipeline.push({
          $unset: ['availability', 'existingBookings', 'activeBookings', 'medProfRef']
        })

        // Add availability priority sorting (today=1, tomorrow=2, later=3+)
        pipeline.push({
          $addFields: {
            availabilityPriority: {
              $cond: [
                { $ne: ['$nextAvailableSlot', null] },
                {
                  $let: {
                    vars: {
                      today: { $dateToString: { format: '%Y-%m-%d', date: '$$NOW' } },
                      tomorrow: {
                        $dateToString: {
                          format: '%Y-%m-%d',
                          date: {
                            $dateAdd: {
                              startDate: '$$NOW',
                              unit: 'day',
                              amount: 1
                            }
                          }
                        }
                      },
                      slotDate: '$nextAvailableSlot.date'
                    },
                    in: {
                      $cond: [
                        { $eq: ['$$slotDate', '$$today'] },
                        1, // Today - highest priority
                        {
                          $cond: [
                            { $eq: ['$$slotDate', '$$tomorrow'] },
                            2, // Tomorrow - second priority
                            3 // Later dates - lower priority
                          ]
                        }
                      ]
                    }
                  }
                },
                999 // No availability - lowest priority
              ]
            }
          }
        })

        // Sort by availability priority first, then by original sort order
        pipeline.push({
          $sort: {
            availabilityPriority: 1,
            dateCreated: -1
          }
        })

        // Remove the temporary priority field
        pipeline.push({
          $unset: 'availabilityPriority'
        })

        // Execute aggregation
        if (isPwa) {
          response = await getDataWithSync(STORES.SYSTEM_USERS, {
            body: {
              pipeline: JSON.stringify(pipeline)
            },
            requestConfig: {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'profile-key': SystemUsersEnums.profileKeys.AUTH_USERS,
                'route-key': SystemUsersEnums.routeKeys.AGGREGATE
              }
            }
          })
        } else {
          response = await agilite.Connectors.execute(
            SystemUsersEnums.profileKeys.AUTH_USERS,
            SystemUsersEnums.routeKeys.AGGREGATE,
            { pipeline: JSON.stringify(pipeline) }
          )
          response = response.data
        }

        // Process location-based search if user location is available
        if (filter?.userLocation && filter.userLocation.success && Array.isArray(response)) {
          const maxDistance = filter.maxDistance || 50 // Default to 50km if not specified

          // Add distance information to each doctor's clinic
          const doctorsWithDistance = response.map((doctor) => {
            if (doctor.clinicRecord && doctor.clinicRecord.geolocation) {
              const distance = require('../utils/helpers').calculateDistance(
                filter.userLocation.lat,
                filter.userLocation.lng,
                doctor.clinicRecord.geolocation.coordinates[1], // lat
                doctor.clinicRecord.geolocation.coordinates[0] // lng
              )

              return {
                ...doctor,
                clinicRecord: {
                  ...doctor.clinicRecord,
                  distance,
                  distanceFormatted: distance ? `${distance.toFixed(1)}km away` : 'Distance unknown'
                }
              }
            }
            return doctor
          })

          // Filter by maximum distance
          const filteredDoctors = doctorsWithDistance.filter((doctor) => {
            const distance = doctor.clinicRecord?.distance
            return distance === null || distance === undefined || distance <= maxDistance
          })

          // Sort by distance (closest first)
          const sortedDoctors = filteredDoctors.sort((a, b) => {
            const distanceA = a.clinicRecord?.distance || Infinity
            const distanceB = b.clinicRecord?.distance || Infinity
            return distanceA - distanceB
          })

          console.log(
            `Location-based search completed. Found ${sortedDoctors.length} doctors within ${maxDistance}km.`,
            sortedDoctors[0]?.clinicRecord?.distance
              ? `Closest clinic: ${sortedDoctors[0].clinicRecord.distance.toFixed(1)}km away`
              : 'No clinics found within range'
          )

          resolve(sortedDoctors)
        } else {
          resolve(response)
        }
      } catch (e) {
        if (e.response?.data?.errorMessage) {
          reject(e.response.data.errorMessage)
        } else if (e.message) {
          reject(e.message)
        } else {
          reject(CoreEnums.errorMessages.UNKNOWN_ERROR)
        }
      }
    })()
  })
}

export const readSearchTypes = () => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        const response = await agilite.Keywords.getValuesByProfileKey('search_type_options')
        resolve(response.data)
      } catch (e) {
        console.error('Error fetching search types:', e)
        reject(handleError(e, true))
      }
    })()
  })
}

const handleSearchRequest = async (data) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        const response = await axios.post(`${process.env.REACT_APP_NODE_RED_URL}/api/search-engine`, {
          ...data,
          entityRef: store.getState().core.entity._id
        })
        resolve(response.data)
      } catch (e) {
        console.error('Node-RED search request failed:', e)
        reject(handleError(e, true))
      }
    })()
  })
}

const handleIndividualAvailabilityFetch = async (data) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        const response = await axios.post(`${process.env.REACT_APP_NODE_RED_URL}/api/professional-availability`, data)
        resolve(response.data)
      } catch (e) {
        console.error('Node-RED availability request failed:', e)
        reject(handleError(e, true))
      }
    })()
  })
}
